import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, FilterIcon, LinkIcon, ImageIcon, InstagramIcon, LinkedinIcon, MagnetIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, SparklesIcon, TargetIcon, TrendingUpIcon, UsersIcon, VideoIcon, YoutubeIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { SOCIAL_NETWORKS } from '../../data/socialNetworks';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon,
  Bot: BotIcon,
  Magnet: MagnetIcon,
  Filter: FilterIcon,
  TrendingUp: TrendingUpIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
  /**
   * Dispositivo simulado en la vista previa del constructor. Con 'mobile' se
   * aplican los mismos ajustes de móvil que en la landing pública.
   */
  viewport?: 'desktop' | 'tablet' | 'mobile';
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};

/**
 * Destino del CTA principal: el formulario multipaso de cualificación. Si la
 * landing no lo incluye, baja al calendario.
 */
function scrollToForm() {
  const target = document.getElementById('formulario') ?? document.getElementById('calendario');
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/**
 * Ajustes de móvil del bloque: se publican como variables CSS y una clase por
 * propiedad definida, para que `index.css` las aplique solo bajo 640 px. Así la
 * versión móvil se edita sin alterar escritorio ni tablet, y el constructor y
 * la landing pública comparten exactamente las mismas reglas.
 */
function deviceStyle(block: LandingBlock, device: 'm' | 't'): {
  vars: Record<string, string>;
  className: string;
} {
  const s = device === 'm' ? block.style?.mobile : block.style?.tablet;
  if (!s || s.sync !== false) return {
    vars: {},
    className: ''
  };
  const vars: Record<string, string> = {};
  const classes: string[] = [];
  const add = (key: string, value: string | undefined) => {
    if (value === undefined) return;
    vars[`--${device}-${key}`] = value;
    classes.push(`ava-${device}-${key}`);
  };
  add('title', s.titleSize !== undefined ? `${s.titleSize}px` : undefined);
  add('body', s.bodySize !== undefined ? `${s.bodySize}px` : undefined);
  add('lh', s.lineHeight !== undefined ? String(s.lineHeight) : undefined);
  add('ls', s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined);
  add('weight', s.titleWeight !== undefined ? String(s.titleWeight) : undefined);
  add('align', s.align);
  add('py', s.paddingY !== undefined ? `${s.paddingY}px` : undefined);
  add('px', s.paddingX !== undefined ? `${s.paddingX}px` : undefined);
  return {
    vars,
    className: classes.join(' ')
  };
}

/** Ajustes responsive (móvil + tablet) combinados de un bloque. */
function mobileStyle(block: LandingBlock): {
  vars: React.CSSProperties;
  className: string;
} {
  const mobile = deviceStyle(block, 'm');
  const tablet = deviceStyle(block, 't');
  return {
    vars: {
      ...mobile.vars,
      ...tablet.vars
    } as React.CSSProperties,
    className: `${mobile.className} ${tablet.className}`.trim()
  };
}

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 w-full rounded-xl px-8 transition-transform duration-200 sm:w-auto ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-451" data-mp="3icqvsvazi72d" /> : undefined} data-id="element-450" data-mp="34e0zhhvzobf2">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-452" data-mp="25zsoiuoym907">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-453" data-mp="212tuec5aq220">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-454" data-mp="1s013ia00zscp">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-455" data-mp="lkqm8u0whr6r">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-456" data-mp="gkvw4soy95mr" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-457" data-mp="2sb3r5p5162gb" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-458" data-mp="31a7b0vc0ulvv">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock,
  viewport = 'desktop'
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Al seleccionar un bloque en el constructor, la vista previa se desplaza
  // suavemente hasta él para mantener panel y preview sincronizados.
  React.useEffect(() => {
    if (!selectedBlockId || !onSelectBlock) return;
    const target = rootRef.current?.querySelector(`[data-block-id="${selectedBlockId}"]`);
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [selectedBlockId, onSelectBlock]);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30) {
    return <BrandLogo src={config.logoUrl} size={height} alt={landing.name} data-id="element-459" data-mp="2vyuv7uc1ic2e" />;
  }
  const brandMark = mark();
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        {
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const centered = cfg(block, 'logoAlign', 'center') === 'center';
          const showLogo = flag(block, 'showLogo', true);
          const logoHeight = Number(cfg(block, 'logoHeight', 34)) || 34;
          // El acceso «Iniciar sesión» se puede ocultar desde el editor sin
          // dejar espacio vacío: si no hay nada que mostrar, no se renderiza.
          const showAuth = authLinks && flag(block, 'showAuth', true);
          const actions = <div className="flex items-center gap-2 sm:gap-4" data-id="element-460" data-mp="1cwj00bgtrn9u">
            {showAuth && <Link to="/login" className="whitespace-nowrap text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:text-sm" data-id="element-461" data-mp="17yheljvxfbv7">
                Iniciar sesión
              </Link>}
            {ctaLabel && <Button size="sm" onClick={scrollToForm} className="whitespace-nowrap rounded-full px-4 text-[13px] sm:px-5 sm:text-sm" data-id="element-462" data-mp="y3za4ciorcac">
                {ctaLabel}
              </Button>}
          </div>;
          const hasActions = Boolean(ctaLabel) || showAuth;
          return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-463" data-mp="1lxzaej7n3pe2">
            {centered ?
            // Logo centrado: las acciones se reservan su propio espacio a
            // izquierda y derecha, así nunca se solapan con el logotipo.
            <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-3 sm:gap-3 sm:px-8 sm:py-4" data-id="element-464" data-mp="29ntkit0kqzum">
                <span aria-hidden="true" data-id="element-465" data-mp="cqp723y34grs" />
                <div className="flex min-w-0 justify-center" data-id="element-466" data-mp="cpdubemnvyoj">{showLogo && mark(logoHeight)}</div>
                <div className="flex justify-end" data-id="element-467" data-mp="3u65eganirfnn">{hasActions && actions}</div>
              </div> : <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-8 sm:py-4" data-id="element-468" data-mp="30411vg309yu">
                {showLogo ? mark(logoHeight) : <span data-id="element-469" data-mp="3l8v4csmftwkb" />}
                {hasActions && actions}
              </div>}
          </header>;
        }
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-470" data-mp="3b949uqxvnhk8">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-5 pb-4 pt-9 text-center sm:px-8 sm:pb-6 sm:pt-24" data-id="element-471" data-mp="1egkkdzurbaqz">
            <div className="mx-auto w-full max-w-4xl" data-id="element-472" data-mp="2yxk23zaa454q">
              {flag(block, 'showLogo', false) && <div className="mb-8 flex justify-center sm:mb-12" data-id="element-473" data-mp="2tz8rhmc5tqlg">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary sm:text-xs sm:tracking-[0.3em]" data-id="element-474" data-mp="38pzfskejm7jt">
                  {eyebrow}
                </p>}
              {cfg(block, 'brandLine', '') && <p className="mx-auto mt-2.5 max-w-[19rem] text-[10px] font-bold uppercase leading-[1.5] tracking-[0.14em] text-navy sm:mt-4 sm:max-w-none sm:text-[13px] sm:tracking-[0.3em]" data-id="element-475" data-mp="3hqe31pb2hwpy">
                  {cfg(block, 'brandLine', '')}
                </p>}
              {/*
                Móvil: tamaño fluido (30–38 px según el ancho real del
                dispositivo) e interlineado 1.12, para que el titular ocupe
                menos líneas y no se parta de forma poco elegante en 375–430 px.
                Los saltos de línea manuales de la plantilla se respetan solo
                desde tablet: en móvil el texto fluye y decide sus propios
                cortes. Escritorio queda exactamente igual que antes.
               */}
              <h1 className="mx-auto mt-4 max-w-3xl whitespace-normal text-[clamp(30px,7.6vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-navy sm:mt-9 sm:whitespace-pre-line sm:text-[54px] sm:font-bold sm:leading-[1.08] sm:tracking-[-0.025em]" data-id="element-476" data-mp="tbxh4rapjkpp">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-477" data-mp="5lb9v28j24gr" />
              </h1>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-xl text-[16px] leading-[1.6] text-muted-foreground sm:mt-8 sm:text-base sm:leading-relaxed" data-id="element-478" data-mp="9lu0wu2lj6hw">
                  <Highlighted text={cfg(block, 'subtitle', '')} highlight={cfg(block, 'subtitleHighlight', '')} data-id="element-479" data-mp="efpwxqzaujaf" />
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-480" data-mp="3i1dq9i05c4ro">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-481" data-mp="35kvufbzglfvk">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-482" data-mp="31h6efawuo6m5">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-483" data-mp="2rmefa2nmdbp9">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-484" data-mp="2msjwr2jr32rr">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-485" data-mp="2dtpsqr43ba46">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} loading="lazy" decoding="async" className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-486" data-mp="28ssbqhutgm49" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-487" data-mp="1u7egsi1u3kq1">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        {
          const num = (key: string): number | undefined => {
            const raw = String(block.config[key] ?? '').trim();
            if (raw === '') return undefined;
            const parsed = Number.parseInt(raw, 10);
            return Number.isFinite(parsed) ? parsed : undefined;
          };
          const frame = {
            radius: num('radius'),
            radiusTablet: num('radiusTablet'),
            radiusMobile: num('radiusMobile'),
            radiusCustom: cfg(block, 'radiusCustom', ''),
            borderWidth: num('borderWidth'),
            borderColor: cfg(block, 'borderColor', ''),
            shadow: (cfg(block, 'shadowStyle', '') || undefined) as 'ninguna' | 'suave' | 'media' | 'fuerte' | undefined,
            maxWidth: num('maxWidth')
          };
          const gap = num('gap');
          return <section key={block.id} className="px-4 pb-4 pt-7 sm:px-8 sm:pb-8 sm:pt-10" style={gap === undefined ? undefined : {
            paddingTop: gap,
            paddingBottom: gap
          }} data-id="element-488" data-mp="k8c1uk083e8n">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '') ? <Highlighted text={cfg(block, 'overlayTitle', '')} highlight={cfg(block, 'overlayHighlight', '')} data-id="element-490" data-mp="6b6yirrw8th3" /> : undefined} maxWidthClass="max-w-[760px]" editing={preview} frame={frame} data-id="element-489" data-mp="u09gk0qfwoc4" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-491" data-mp="b8oxpqg3zubw" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-492" data-mp="1qp77t8yze44j">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mx-auto mt-9 flex max-w-[380px] justify-center" data-id="element-493" data-mp="1mpyeu6brmaa1">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-494" data-mp="3g85rcdulhnd4" />
              </div>}
          </section>;
        }
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-495" data-mp="2romxm7c0b62">
            <div className="mx-auto max-w-6xl" data-id="element-496" data-mp="7q9detws7ghb">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-497" data-mp="7scec8zh2u0t">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-498" data-mp="hnxkmjbkqggd">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-499" data-mp="2jglomuw1l7x7" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-500" data-mp="vseewxc5k8r8">
                        <Icon className="h-6 w-6 text-primary" data-id="element-501" data-mp="mv3y6wgkfrex" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-502" data-mp="1jja03bm3s5pi">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-503" data-mp="1aj537vejcppl">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'pilares':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-10 sm:px-8 sm:py-14" data-id="element-504" data-mp="5t1zkjzsyyte">
            <div className="mx-auto max-w-5xl rounded-3xl bg-surface px-6 py-12 sm:px-10 sm:py-14" data-id="element-505" data-mp="10qqaqj6a0o6">
              <div className="grid gap-10 sm:grid-cols-3 sm:gap-0" data-id="element-506" data-mp="3s71uuwne5jk9">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`px-2 text-center sm:px-7 ${index > 0 ? 'sm:border-l sm:border-border/70' : ''}`} data-id="element-507" data-mp="oql5vfuvm3c4" data-mpi={index}>
                      <Icon className="mx-auto mb-6 h-9 w-9 text-primary" aria-hidden="true" data-id="element-508" data-mp="jsjiwjubxjvd" />
                      <h3 className="mb-4 text-lg font-bold text-navy" data-id="element-509" data-mp="equ731za9268">
                        {item.title}
                      </h3>
                      <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground" data-id="element-510" data-mp="2ziwcuhnq28o4">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'frase':
        return <section key={block.id} className="px-5 py-12 sm:px-8 sm:py-16" data-id="element-511" data-mp="1f0vc9rkxzni3">
            <div className="mx-auto flex max-w-4xl items-center gap-5 sm:gap-8" data-id="element-512" data-mp="1eysmn8npbwdw">
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-513" data-mp="1jydhp7zr3klc" />
              <p className="flex-1 whitespace-pre-line text-center text-[11px] font-bold uppercase leading-relaxed tracking-[0.16em] text-navy sm:flex-none sm:text-sm sm:tracking-[0.18em]" data-id="element-514" data-mp="1os9fa9c3r5fz">
                {cfg(block, 'text', '')}
              </p>
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-515" data-mp="22qpktwy8apwj" />
            </div>
          </section>;
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-516" data-mp="dbi2ldd9ia0u">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-517" data-mp="ddm4xqvij0z0">
              {items.map(item => <div key={item.label} data-id="element-518" data-mp="5cn5oorotmc7">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-519" data-mp="1a0b4g3mtnbb">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-520" data-mp="f6wyg7kjkmng">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-521" data-mp="t0oapnpnqkmv">
            <div className="mx-auto max-w-6xl" data-id="element-522" data-mp="17x5jq8adjn69">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-523" data-mp="17xx40myityh8">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-524" data-mp="1mlp4jpjt7uh9">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-525" data-mp="1qm90ipsncs1t">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-526" data-mp="1coam5kebsvmb">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-527" data-mp="17n3ermg3hyg6">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-528" data-mp="tx9rv9o5ho93">
                      {item.avatar ? <img src={item.avatar} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover" data-id="element-529" data-mp="ysp4jyaf9e3x" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-530" data-mp="owm18p3u39fe">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-531" data-mp="1n86hhg8mgxxh">
                        <span className="block truncate font-semibold text-foreground" data-id="element-532" data-mp="1ihq2vst2f69j">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-533" data-mp="1dhtuzih8yuop">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-534" data-mp="14gvq6xyzij52">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-535" data-mp="2oyl98ivqjy5y">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-536" data-mp="2wi2wo7c3p87s">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-537" data-mp="367e4zrr5y8um">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-538" data-mp="3b55ukf4c5ivn">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-539" data-mp="2ihs5t4asjtmm">
            <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-12" data-id="element-540" data-mp="2nizct1540os8">
              <h2 className="text-[26px] font-semibold leading-tight tracking-tight text-navy sm:text-4xl" data-id="element-541" data-mp="2sh0w9n6g6q48">
                {cfg(block, 'title', 'Solicita información')}
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-lg" data-id="element-542" data-mp="7nxf21x2yv43">
                {cfg(block, 'subtitle', '')}
              </p>
            </div>
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-543" data-mp="1ysh7flgocxr9" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-544" data-mp="28rgex0ppenco">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-545" data-mp="1p2o4f8ullqym">
            <div className="mx-auto max-w-3xl" data-id="element-546" data-mp="11dczdf67taeo">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-547" data-mp="11c1vzggom4o3">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-548" data-mp="n7bf7ou9onca">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-549" data-mp="rzl2sy7wtbbe">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-550" data-mp="d8luloazkosc">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-551" data-mp="1axovyj7eizrk" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-552" data-mp="10yq0y3fk72n1">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        {
          // La reserva usa el calendario global de la empresa salvo que la
          // landing tenga uno propio. La lógica de reserva no cambia.
          const custom = cfg(block, 'calendarSource', 'global') === 'custom';
          const bookingUrl = String((custom ? cfg(block, 'url', '') : cfg(block, 'globalUrl', '') || cfg(block, 'url', '')) as string).trim();
          // Sin enlace válido el bloque no se muestra en la landing pública:
          // así nunca queda un hueco ni se carga ningún script de calendario.
          if (!bookingUrl && !preview) return null;
          const embedded = cfg(block, 'calendarMode', 'boton') === 'incrustado' && Boolean(bookingUrl);
          const embedHeight = Number(cfg(block, 'embedHeight', 700)) || 700;
          if (embedded) {
            return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-553" data-mp="8bu6pjr56lb2">
              <div className="mx-auto w-full max-w-3xl text-center" data-id="element-554" data-mp="3jj58cgr6rge">
                <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-555" data-mp="1m7lru158bxn6">
                  {cfg(block, 'title', 'Agenda una llamada')}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-556" data-mp="1r8jaeeu5itp7">
                  {cfg(block, 'text', '')}
                </p>
                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-background shadow-soft" data-id="element-557" data-mp="208odicsbb5xk">
                  <iframe src={bookingUrl} title={cfg(block, 'title', 'Calendario de reservas')} className="w-full" style={{
                    height: `${embedHeight}px`
                  }} loading="lazy" data-id="element-558" data-mp="2543rd4qbl1tz" />
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-559" data-mp="2iwte5bdoaas5">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-border bg-background p-7 text-center shadow-soft sm:p-12" data-id="element-560" data-mp="2nuuw1szd4421">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" aria-hidden="true" data-id="element-561" data-mp="3t6ndns03i0fx" />
              <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-562" data-mp="20vpuz9sizwa">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-563" data-mp="3k8l60tluw9jo">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-9 flex justify-center" data-id="element-564" data-mp="gq1jm7puzxws">
                <Button size="lg" className="h-14 w-full rounded-xl px-8 sm:w-auto" onClick={() => {
                  if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener');
                }} data-id="element-565" data-mp="krmtrlb0dy3a">
                  {cfg(block, 'ctaLabel', 'Reservar hora')}
                </Button>
              </div>
              {!bookingUrl && <p className="mt-5 text-xs text-muted-foreground" data-id="element-566" data-mp="znm0arigzq6b">
                  Conecta Google Calendar en Integraciones o añade una URL de
                  reserva a este bloque.
                </p>}
            </div>
          </section>;
        }
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-567" data-mp="19t42wmmbvi7o">
            <MessageCircleIcon className="h-6 w-6" data-id="element-568" data-mp="qu86d4mzyrqo" />
          </a>;
      case 'cta_final':
        {
          // Con número de WhatsApp el botón abre el chat; si no, baja al formulario.
          const waNumber = String(cfg(block, 'whatsappNumber', '')).replace(/\D/g, '');
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-569" data-mp="2g7v995u7zv93">
            <div className="mx-auto max-w-3xl text-center" data-id="element-570" data-mp="2b9tf9pv9abiu">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-571" data-mp="2bbeazitb40ia">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-572" data-mp="2nate00hfn257">
                {cfg(block, 'text', '')}
              </p>
              <div className="mx-auto mt-10 flex max-w-[380px] justify-center" data-id="element-573" data-mp="2sb7corx58zqk">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={() => {
                  if (waNumber) {
                    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(cfg(block, 'whatsappMessage', ''))}`, '_blank', 'noopener');
                    return;
                  }
                  scrollToForm();
                }} data-id="element-574" data-mp="2x8pnknuq1p17" />
              </div>
            </div>
          </section>;
        }
      case 'footer':
        {
          // Solo se muestran las redes con URL configurada.
          const socials = cfg<{
            network: string;
            url: string;
          }[]>(block, 'socials', []).filter(s => s.url?.trim());
          return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-575" data-mp="1ea1fnmecyqex">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-576" data-mp="19bq5tbo2cfwp">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-577" data-mp="14bc225thvs4h">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-578" data-mp="zg6c8vwz1tb4">
                <p data-id="element-579" data-mp="qlib9hyfixs4">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-580" data-mp="qkqfa60x6y1b">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-581" data-mp="lkb2pgogpuke">
                      {link}
                    </span>)}
                </div>
              </div>
              {socials.length > 0 && <div className="flex gap-3 text-muted-foreground" data-id="element-582" data-mp="bq2bqxf2y9oc">
                  {socials.map((social, index) => {
                  const network = SOCIAL_NETWORKS[social.network];
                  const Icon = network?.icon ?? LinkIcon;
                  return <a key={`${social.network}-${index}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={network?.label ?? social.network} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" data-id="element-583" data-mp="11bbrs25q2tbk" data-mpi={index}>
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-584" data-mp="dl7ppz86n1mo" />
                      </a>;
                })}
                </div>}
            </div>
          </footer>;
        }
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-585" data-mp="1yzlz8woa29a2">
            <div className="mx-auto max-w-6xl" data-id="element-586" data-mp="231gvvnv9jyhi">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-587" data-mp="22ze7f89redew">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-588" data-mp="3jgczeqwqfa6a">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-589" data-mp="3shb4zdmtjpqz">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-590" data-mp="k2sqat2dhbye" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-591" data-mp="2ltmbpwz1kneb">
                        <Icon className="h-5 w-5 text-primary" data-id="element-592" data-mp="2gwe3ao3p3xq9" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-593" data-mp="kt7zxg3eglnb">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-594" data-mp="ptdkydf5z9wf">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-595" data-mp="1dgvjcrsxp68d">
            <div className="mx-auto max-w-4xl" data-id="element-596" data-mp="1ifotqbcxch4b">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-597" data-mp="1iff4ipzpedzo">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-598" data-mp="1rhelxjemrk35">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-599" data-mp="3bzqkjeciso2i" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-600" data-mp="36z2pmhl67n30">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-601" data-mp="32ysd0m2vhyf6" />
                    </span>
                    <span data-id="element-602" data-mp="2y07ldb1osn2c">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-603" data-mp="2y0z7lv9e98fy">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-604" data-mp="3qnbkize3m767">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-605" data-mp="dh1ql0fy5a2s">
            <div className="mx-auto max-w-6xl" data-id="element-606" data-mp="df72jd9dp1uh">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-607" data-mp="3hxjsqv4z3uel">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-608" data-mp="382i37y6scqbh">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-609" data-mp="4jot5vd7ujm2" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-610" data-mp="9ij98pjstu2h">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" loading="lazy" decoding="async" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-611" data-mp="3ro4kbym8byo0" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-612" data-mp="27ul33turha1h">
            <div className="mx-auto max-w-5xl" data-id="element-613" data-mp="2mnohxtgyx9oi">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-614" data-mp="2hqejxpfijpp2">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-615" data-mp="1p320ho4nfxzz">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-616" data-mp="1z06q5xxpeblj">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-617" data-mp="1b8hpnuaqagti" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-618" data-mp="6p3zr1akwklb">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-619" data-mp="giun22uyyozf">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-620" data-mp="bk17m4w4gnwm">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-621" data-mp="3k2pcgmcl84cd">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-622" data-mp="3trh04iam82hh">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-623" data-mp="3oyxldi06z1b3">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-624" data-mp="2wcuy20cme7q0">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-625" data-mp="1btsiwvtm8hxi" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-626" data-mp="12tw2fhhdqpdj">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-627" data-mp="1zjd5dooz9xbj">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-628" data-mp="1ljc4wacmkrwl">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-629" data-mp="1qlb89k8d7otj">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-630" data-mp="f531f6lwhrc2">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-631" data-mp="2gspiod658udp">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-632" data-mp="1y410w02yj565">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-633" data-mp="1t5zdj2yi0jov" />
                      <span className="break-words text-sm text-foreground" data-id="element-634" data-mp="1o6mkg0h1agdu">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const mobile = mobileStyle(block);
    const style = {
      ...wrapperStyle(block),
      ...mobile.vars
    };
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle || mobile.className ? <div key={block.id} style={style} className={mobile.className} data-id="element-635" data-mp="1dzto6ydg2wz3">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} data-block-id={block.id} style={style} className={`relative cursor-pointer outline-none transition-shadow ${mobile.className} ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-636" data-mp="ma8f3xkty02x">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-637" data-mp="2596cmlibt3ud">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div ref={rootRef} className={`w-full bg-background ${viewport === 'mobile' ? 'ava-viewport-mobile' : viewport === 'tablet' ? 'ava-viewport-tablet' : ''}`} style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`
  } as React.CSSProperties} data-id="element-638" data-mp="2e6gp2a6vqz0v">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-639" data-mp="2xvhavpigmso0">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}